import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { ConfirmModal, type ConfirmModalProps } from '@/components/ConfirmModal';
import { FormModal, type FormModalField, type FormModalProps } from '@/components/FormModal';

// ── Types ──────────────────────────────────────────────────────────────

export interface ConfirmOptions {
  title?: string;
  message?: string;
  messageHtml?: string;
  confirmText?: string;
  cancelText?: string;
  confirmClass?: string;
  showCancel?: boolean;
  theme?: string;
}

export interface PromptOptions {
  title?: string;
  fields: FormModalField[];
  confirmText?: string;
  cancelText?: string;
  theme?: string;
  wide?: boolean;
}

interface ModalContextValue {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  prompt: (options: PromptOptions) => Promise<Record<string, any> | null>;
}

const ModalContext = createContext<ModalContextValue>({
  confirm: () => Promise.resolve(false),
  prompt: () => Promise.resolve(null),
});

// ── Internal state ─────────────────────────────────────────────────────

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve: (value: boolean) => void;
}

interface PromptState extends PromptOptions {
  open: boolean;
  resolve: (value: Record<string, any> | null) => void;
}

// ── Provider ───────────────────────────────────────────────────────────

export function ModalProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = useState<PromptState | null>(null);

  const confirmResolveRef = useRef<((v: boolean) => void) | null>(null);
  const promptResolveRef = useRef<((v: Record<string, any> | null) => void) | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      confirmResolveRef.current = resolve;
      setConfirmState({ ...options, open: true, resolve });
    });
  }, []);

  const prompt = useCallback((options: PromptOptions): Promise<Record<string, any> | null> => {
    return new Promise<Record<string, any> | null>((resolve) => {
      promptResolveRef.current = resolve;
      setPromptState({ ...options, open: true, resolve });
    });
  }, []);

  const handleConfirmDone = useCallback((confirmed: boolean) => {
    confirmResolveRef.current?.(confirmed);
    confirmResolveRef.current = null;
    setConfirmState(null);
  }, []);

  const handlePromptDone = useCallback((values: Record<string, any> | null) => {
    promptResolveRef.current?.(values);
    promptResolveRef.current = null;
    setPromptState(null);
  }, []);

  // Register imperative APIs so non-React code can call confirm/prompt
  useEffect(() => {
    registerImperativeModal(confirm, prompt);
    return () => {
      registerImperativeModal(null as any, null as any);
    };
  }, [confirm, prompt]);

  return (
    <ModalContext.Provider value={{ confirm, prompt }}>
      {children}
      {confirmState?.open &&
        createPortal(
          <ConfirmModal
            open={true}
            title={confirmState.title}
            message={confirmState.message}
            messageHtml={confirmState.messageHtml}
            confirmText={confirmState.confirmText}
            cancelText={confirmState.cancelText}
            confirmClass={confirmState.confirmClass}
            showCancel={confirmState.showCancel}
            theme={confirmState.theme}
            onConfirm={() => handleConfirmDone(true)}
            onCancel={() => handleConfirmDone(false)}
          />,
          document.body
        )}
      {promptState?.open &&
        createPortal(
          <FormModal
            open={true}
            title={promptState.title}
            fields={promptState.fields}
            confirmText={promptState.confirmText}
            cancelText={promptState.cancelText}
            theme={promptState.theme}
            wide={promptState.wide}
            onConfirm={(values) => handlePromptDone(values)}
            onCancel={() => handlePromptDone(null)}
          />,
          document.body
        )}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextValue {
  return useContext(ModalContext);
}

// ── Imperative API for non-React code ──────────────────────────────────

let _imperativeConfirm: ModalContextValue['confirm'] | null = null;
let _imperativePrompt: ModalContextValue['prompt'] | null = null;

export function registerImperativeModal(
  confirmFn: ModalContextValue['confirm'],
  promptFn: ModalContextValue['prompt']
) {
  _imperativeConfirm = confirmFn;
  _imperativePrompt = promptFn;
}

export function imperativeConfirm(options: ConfirmOptions): Promise<boolean> {
  if (_imperativeConfirm) return _imperativeConfirm(options);
  return Promise.resolve(false);
}

export function imperativePrompt(options: PromptOptions): Promise<Record<string, any> | null> {
  if (_imperativePrompt) return _imperativePrompt(options);
  return Promise.resolve(null);
}
